/**
 * Turns paragraph/line-break HTML into decoded, tag-free paragraph strings (`htmlToParagraphs`), and
 * prepares description HTML for rendering with its markup kept (`normalizeDescriptionHtml`) — no DOM,
 * no innerHTML.
 */

// Boundaries are only </p>, <p…> (incl. bare <p/>) and <br…>; newlines are plain whitespace.
const PARAGRAPH_BOUNDARY = /<\/?(?:p|br)(?![a-zA-Z0-9])[^>]*>/gi;
const TAG_PATTERN = /<[^>]*>/g;
const ENTITY_PATTERN = /&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi;
const MAX_CODE_POINT = 0x10ffff;
const SURROGATE_RANGE_START = 0xd800;
const SURROGATE_RANGE_END = 0xdfff;

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
  mdash: "—",
  ndash: "–",
  hellip: "…",
};

function decodeNumericEntity(body: string): string | null {
  const isHex = body[1] === "x" || body[1] === "X";
  const codePoint = Number.parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);

  const isSurrogate = codePoint >= SURROGATE_RANGE_START && codePoint <= SURROGATE_RANGE_END;

  if (!Number.isInteger(codePoint) || codePoint <= 0 || codePoint > MAX_CODE_POINT || isSurrogate)
    return null;
  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return null;
  }
}

function decodeEntities(text: string): string {
  return text.replace(ENTITY_PATTERN, (match, body: string) => {
    if (body[0] === "#") return decodeNumericEntity(body) ?? match;
    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });
}

export function htmlToParagraphs(html: string | null | undefined): string[] {
  if (!html) return [];

  return html
    .split(PARAGRAPH_BOUNDARY)
    .map((segment) => decodeEntities(segment.replace(TAG_PATTERN, "")))
    .map((segment) => segment.replace(/\s+/g, " ").trim())
    .filter((segment) => segment.length > 0);
}

// Shared pattern fragments. A <br> tag of any spelling (<br>, <br/>, <br />) but not <brx>.
const BR_TAG = "<br(?![a-zA-Z0-9])[^>]*>";
// Whitespace or a no-break space (&nbsp; or its numeric spellings) — content that renders as nothing.
const BLANK_CHAR = "\\s|&nbsp;|&#160;|&#xa0;";
// A <p> holding only whitespace, &nbsp; or <br> — a Shopify spacer, not content.
const SPACER_PARAGRAPH = new RegExp(`<p(?:\\s[^>]*)?>(?:${BLANK_CHAR}|${BR_TAG})*</p>`, "gi");
const BLOCK_NAME = "(?:p|div|ul|ol|li|h[1-6]|table|blockquote)";
// The tag name must end on a boundary so <picture>, <progress> and <link> are not read as p / li.
const BLOCK_ELEMENT = new RegExp(`</?${BLOCK_NAME}(?=[\\s/>])`, "i");
// A <br> with only whitespace around it between a closing and an opening block; $1 keeps the closer.
const BARE_BREAK_BETWEEN_BLOCKS = new RegExp(
  `(</${BLOCK_NAME}\\s*>\\s*)${BR_TAG}(?=\\s*<${BLOCK_NAME}(?=[\\s/>]))`,
  "gi",
);
// A <br> or a blank line splits; a single newline is plain whitespace, as it is in the browser.
const LINE_BREAK = new RegExp(`${BR_TAG}|\\r?\\n[ \\t]*\\r?\\n`, "i");
const BLANK_PIECE = new RegExp(`^(?:${BLANK_CHAR})*$`, "i");
// A tag never contains < or >; a newline inside one (before an attribute) is not a line break.
const TAG_TOKEN = /<[^<>]+>/g;

/**
 * Prepares Shopify description HTML for `parse`, markup kept: drops spacer paragraphs (they would
 * double the paragraph gap). A body with block elements also loses a bare <br> between two blocks;
 * a body without any makes one <p> per piece between <br>s and blank lines.
 */
export function normalizeDescriptionHtml(html: string | null | undefined): string {
  if (!html?.trim()) return "";

  const withoutSpacers = html.replace(SPACER_PARAGRAPH, "");
  if (BLOCK_ELEMENT.test(withoutSpacers))
    return withoutSpacers.replace(BARE_BREAK_BETWEEN_BLOCKS, "$1");

  // Flatten whitespace inside tags first so the split cannot cut one in half.
  return withoutSpacers
    .replace(TAG_TOKEN, (tag) => tag.replace(/\s+/g, " "))
    .split(LINE_BREAK)
    .map((piece) => piece.trim())
    .filter((piece) => !BLANK_PIECE.test(piece))
    .map((piece) => `<p>${piece}</p>`)
    .join("");
}
